import {Component, EventEmitter, Injector, Input, OnDestroy, OnInit, Output} from '@angular/core';
import {
    BreadcrumbItem,
    BreadcrumbItemBuilder,
    DefaultSignatoriesProvider,
    EdocEventType,
    EdocMessage,
    EdocOptions,
    PesDocument,
    PesEventUtils,
    PesModalService,
    PesTask,
    PesTaskService,
    ScrollService,
    SignatoriesProvider,
    SignMode,
    Step,
    SubscriptionManager
} from '@pes/ui';
import {ISubscription} from 'rxjs/Subscription';
import {TranslateService} from '@ngx-translate/core';
import {Observable} from 'rxjs/Observable';
import {UserActionTracerService} from '../../../utils/service/user-action-tracer.service';
import {DocReadEnum} from '../../../enum/doc-read.enum';
import {concatMap} from 'rxjs/operators/concatMap';
import {finalize} from 'rxjs/operators/finalize';

@Component({
    selector: 'lbpa-validate',
    templateUrl: './validate.component.html'
})
export class ValidateComponent implements OnInit, OnDestroy {
    /**
     * The reference to the event responsible for initializing the list of documents to validate.
     */
    private static readonly INIT_EVENT: string = 'init_validation';
    @Input() public step: any = null;
    @Input() public task: PesTask = null;
    public edocOptions: EdocOptions[] = null;

    public buttonTxt: string = null;

    @Output()
    public gotToSign: EventEmitter<boolean> = new EventEmitter<boolean>();

    @Output()
    public loader: EventEmitter<boolean> = new EventEmitter<boolean>();
    /**
     * The list of documents to validate.
     */
    protected documents: PesDocument[] = null;
    /**
     * The reference to the PES task service.
     */
    protected taskService: PesTaskService = null;
    /**
     * The list of breadcrumb items.
     */
    protected breadcrumbItems: BreadcrumbItem[] = null;
    /**
     * The reference to the current document position in the list of documents to validate.
     */
    protected currentItemNum: number = 0;
    /**
     * The reference to the document currently be displayed.
     */
    protected currentDoc: PesDocument = null;
    /**
     * Indicates the number of document read in the reading required stack.
     */
    protected readDocStackNum: number = 0;
    /**
     * Indicates whether the current document has been read (true), or not (false).
     */
    protected docRead: boolean = false;
    /**
     * The reference to the PES modal service.
     */
    protected modal: PesModalService = null;
    /**
     * A map that contains references for all transactions Edoc options.
     */
    private readonly OPTIONS_MAP: Map<string, EdocOptions> = null;
    /**
     * The reference to the PES translation service.
     */
    private readonly _translate: TranslateService = null;
    /**
     * Reference to the PES ScrollService service.
     */
    private readonly _scrollService: ScrollService = null;

    private readonly signatoriesProvider: SignatoriesProvider;

    private readonly userActionTracerService: UserActionTracerService;

    constructor(protected injector: Injector) {
        this.taskService = injector.get(PesTaskService);
        this.OPTIONS_MAP = new Map<string, EdocOptions>();
        this._translate = injector.get(TranslateService);
        this.modal = injector.get(PesModalService);
        this._scrollService = injector.get(ScrollService);
        this.signatoriesProvider = injector.get(DefaultSignatoriesProvider);
        this.userActionTracerService = injector.get(UserActionTracerService);
        SubscriptionManager.getInstance().createSubscribable(this);
    }

    public ngOnInit(): void {
        this.refresh();
    }

    /**
     * @inheritDoc
     */
    public ngOnDestroy(): void {
        SubscriptionManager.getInstance().deleteSubscribable(this);
    }


    protected refresh(): void {
        const taskRef: string = this.task.reference;

        this.step.transactions.forEach((trId: string) => {
            this.initEdocOptions(trId);
        });

        const sub: ISubscription = this.taskService.sendMessage(taskRef, PesEventUtils.EVENT_REF, {
            event: ValidateComponent.INIT_EVENT,
            step: this.step
        }).subscribe(() => {
            const sub2: ISubscription = this.taskService.get(taskRef).subscribe((task: PesTask) => {
                const builder: BreadcrumbItemBuilder = BreadcrumbItemBuilder.create();
                const stepId: string = this.step.id;
                this.task = task;
                this.step = task.data.steps.find((step: Step) => {
                    return step.id === stepId;
                });
                const technicalContext: any = this.step.technicalContext;
                if (technicalContext && technicalContext.documents) {
                    this.breadcrumbItems = [];
                    this.documents = [];
                    technicalContext.documents.forEach((doc: PesDocument) => {
                        const docName: string = this._translate.instant(doc.configName);
                        this.documents.push(doc);
                        this.breadcrumbItems.push(
                            builder.reset().title(docName).clickEnable(false).build()
                        );
                    });
                    this.currentItemNum = 1;
                    this.setCurrentDoc();
                    this.readDocStackNum = this.documents.length;
                }
            });
            SubscriptionManager.getInstance().registerSubscription(this, sub2);
        });
        SubscriptionManager.getInstance().registerSubscription(this, sub);
    }

    /**
     * Initializes the Edoc options associated with the specified transaction.
     *
     * @param {string} transaction the id of the transaction for which to get the options.
     */
    protected initEdocOptions(transactionId: string): void {
        if (this.step.edocOptions) {
            if (!this.OPTIONS_MAP.has(transactionId)) {
                const options: EdocOptions = this.step.edocOptions.find((opt: EdocOptions) => {
                    return opt.transaction === transactionId;
                });
                this.OPTIONS_MAP.set(transactionId, options);
                if (options && this.documents) {
                    const size: number =
                        this.documents.filter((doc: PesDocument) => doc.transactionId === transactionId).length;
                    this.readDocStackNum += size;
                }
            }
        } else {
            this.OPTIONS_MAP.set(transactionId, null);
        }
    }

    /**
     * Sets the current document, depending on the current item num.
     */
    protected setCurrentDoc(): void {
        this.currentDoc = this.documents[this.currentItemNum - 1];
        this.setButtonTxt();
        this.docRead = false;
    }

    /**
     * Returns a boolean that indicates whether the documents in the specified transaction must be entirely read.
     *
     * @param {string} transactionId the id of the transaction for which to get the force reading option.
     *
     * @returns {boolean} true whether the documents in the specified transaction must be entirely read; false
     *                    otherwise.
     */
    protected readingForced(): boolean {
        let result: boolean = false;
        if (this.currentDoc) {
            result = (this.currentDoc as any).forceReading
        }
        return result;
    }

    /**
     * The event handler that receives edoc events when enableContraliaEvent is true.
     *
     * @param {EdocMessage} message a message event fired by the edoc component.
     */
    protected contraliaEventHandler(message: EdocMessage): void {
        if (message.name === EdocEventType.EDOC_DOCREAD) {
            this.readDocStackNum--;
            this.docRead = true;
        } else if (message.name === EdocEventType.EDOC_PAGELOADED) {
            this.loader.emit(false);
        }
    }

    protected submit(): void {
        this.loader.emit(true);

        this.traceClickAction()
            .pipe(
                finalize(() => {
                    if (this.documents && this.currentItemNum < this.documents.length) {
                        this.breadcrumbItems[this.currentItemNum - 1].clickEnable = true;
                        this.currentItemNum += 1;
                        this.setCurrentDoc();
                        this._scrollService.scrollTo();
                    } else {
                        this.gotToSign.emit();
                    }
                }),
                concatMap(() => {
                    const enumTrace  = !this.docRead ? DocReadEnum.PARTIAL_READ : DocReadEnum.ALL_READ;
                    return this.traceReadDocumentAction(enumTrace);
                })
            ).subscribe();
    }

    protected itemClickedHandler(itemNum: number): void {
        this.currentItemNum = itemNum;
        const itemNumMinus = this.currentItemNum - 1;
        this.currentDoc = this.documents[itemNumMinus];
        this.setButtonTxt();
    }

    private traceReadDocumentAction(docRead: DocReadEnum): Observable<any> {
        const userId = this.signatoriesProvider.getSignatories(SignMode.DISTANT, this.task)[0].user.id;
        return this.userActionTracerService.traceReadDocumentAction(
            this.task.reference,
            this.currentDoc.transactionId,
            userId,
            this.currentDoc.configName,
            docRead
        );
    }

    private traceClickAction(): Observable<any> {
        const userId = this.signatoriesProvider.getSignatories(SignMode.DISTANT, this.task)[0].user.id;
        return this.userActionTracerService.traceClickAction(
            this.task.reference,
            this.currentDoc.transactionId,
            userId,
            this.currentDoc.configName,
            this._translate.instant(this.buttonTxt)
        )
    }

    private setButtonTxt(): void {
        this.buttonTxt = this.currentItemNum < this.documents.length ? 'circuit.lu' : 'circuit.lu_signe';
    }

    forceReading():boolean{
        if (this.task.data.taskData.transactions[0].forceReading){
            if(this.task.data.taskData.transactions[0].forceReading  == "FULL_READING" && this.currentDoc["toSign"]){
                return this.docRead
            }
        }
        return true
    }

}



// WEBPACK FOOTER //
// ./src/app/component/step-parcours-lbpa/validate/validate.component.ts